import React, { FC, Fragment } from 'react';
import Svg from 'svg-inline-react';
import { orderBy } from 'lodash';
import classnames from 'classnames';
import FrInfoSvg from '@src/svgs/fr-info-small.svg';
import { Tooltip } from '@src/components/Tooltip';
import {
  useSenioritiesQueryQuery,
  SenioritiesQueryQuery,
  useGetSpecialitiesQuery,
  GetSpecialitiesQuery,
} from '@src/graphql/generated';
import { Button, Appearance } from '@src/components/Button';
import { RangeSlider } from '@src/components/RangeSlider';
import { DEFAULT_CURRENCY_SYMBOL } from '@src/constants';
import { FilterKeys } from '@src/containers/Source/enums/filter.enum';
import { MAX_SALARY, MIN_SALARY } from '@src/containers/Source/constants/salaryRange';
import { SidebarFiltersProps } from './props';
import './styles.scss';

export const SidebarFilters: FC<SidebarFiltersProps> = ({
  selectedSeniorities,
  selectedSpecialities,
  selectedSalaryRange,
  onFilterSelect,
}) => {
  const { data: senioritiesData, loading: isSenioritiesLoading } = useSenioritiesQueryQuery();

  const { data: specialityData, loading: isSpecialityLoading } = useGetSpecialitiesQuery();

  const renderSkeleton = () => {
    return (
      <Fragment>
        <span className="ta-sidebar-filters-wrapper__filter-button-skeleton pulse-fade" />
        <span className="ta-sidebar-filters-wrapper__filter-button-skeleton pulse-fade" />
        <span className="ta-sidebar-filters-wrapper__filter-button-skeleton pulse-fade" />
      </Fragment>
    );
  };

  return (
    <div className="ta-sidebar-filters-wrapper">
      <div className="ta-sidebar-filters-wrapper__section">
        <div className="ta-sidebar-filters-wrapper__section-header">
          Salary range
          <Tooltip overlay="Gross salary per month" placement="top">
            <Svg className="ta-sidebar-filters-wrapper__info-icon" src={FrInfoSvg} />
          </Tooltip>
        </div>
        <div className="ta-sidebar-filters-wrapper__section-body">
          <RangeSlider
            defaultValue={selectedSalaryRange || [MIN_SALARY, MAX_SALARY]}
            leftLabelPrefix={DEFAULT_CURRENCY_SYMBOL}
            max={MAX_SALARY}
            min={MIN_SALARY}
            onAfterChange={(activeSalaryRange: number[]) =>
              onFilterSelect({
                key: FilterKeys.SALARY_RANGE,
                value: activeSalaryRange,
              })
            }
            rightLabelPrefix={DEFAULT_CURRENCY_SYMBOL}
            rightLabelSuffix="+"
            step={100}
          />
        </div>
      </div>

      <div className="ta-sidebar-filters-wrapper__section">
        <div className="ta-sidebar-filters-wrapper__section-header">
          Seniority
          {selectedSeniorities.length > 0 && <span>{selectedSeniorities.length}</span>}
        </div>
        <div className="ta-sidebar-filters-wrapper__section-body">
          {isSenioritiesLoading && renderSkeleton()}
          {senioritiesData?.seniorities?.map((item: SenioritiesQueryQuery['seniorities'][0]) => {
            if (!item) {
              return null;
            }

            return (
              <Button
                appearance={Appearance.INVISIBLE}
                className={classnames(
                  'ta-sidebar-filters-wrapper__filter-button',
                  selectedSeniorities.includes(item?.id) && 'ta-sidebar-filters-wrapper__filter-button--active',
                )}
                key={item?.key}
                onClick={() =>
                  onFilterSelect({
                    key: FilterKeys.SENIORITY,
                    value: { id: item.id, name: item.name },
                  })
                }
              >
                {item?.name}
              </Button>
            );
          })}
        </div>
      </div>

      <div className="ta-sidebar-filters-wrapper__section">
        <div className="ta-sidebar-filters-wrapper__section-header">
          Speciality
          {selectedSpecialities.length > 0 && <span>{selectedSpecialities.length}</span>}
        </div>
        <div className="ta-sidebar-filters-wrapper__section-body">
          {isSpecialityLoading && renderSkeleton()}
          {orderBy(specialityData?.specialities, [speciality => speciality?.name.toLowerCase()], ['asc']).map(
            (item: GetSpecialitiesQuery['specialities'][0]) => {
              if (!item) {
                return null;
              }

              return (
                <Button
                  appearance={selectedSpecialities.includes(item.id) ? Appearance.PRIMARY : Appearance.INVISIBLE}
                  className={classnames(
                    'ta-sidebar-filters-wrapper__filter-button',
                    selectedSeniorities.includes(item.id) && 'ta-sidebar-filters-wrapper__filter-button--active',
                  )}
                  key={item?.key}
                  onClick={() =>
                    onFilterSelect({
                      key: FilterKeys.SPECIALITY,
                      value: { id: item.id, name: item.name },
                    })
                  }
                >
                  {item?.name}
                </Button>
              );
            },
          )}
        </div>
      </div>
    </div>
  );
};
